import { Cancel, InfoOutlined } from "@mui/icons-material";
import { Grid, IconButton, Typography } from "@mui/material";
import FilterBuilderAddFilterSelect, {
    type FilterBuilderAddFilterGroup,
    type FilterBuilderAddFilterOption,
} from "components/ApplicationFilters/FiltersAndChips/BaseFilterComponents/FilterBuilderAddFilterSelect";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import _ from "lodash";
import moment from "moment";
import { observer } from "mobx-react";
import React, { useCallback, useState } from "react";
import type {
    ApplicationFilters,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    ApplicationFiltersStore,
    buildExtendedMetadataGroupedFromStrings,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import BaseObjectStore from "../BaseObjectStore";
import { getObjectFilterKeysFromFilters } from ".";
import theme from "Theme/AppTheme";

type SavedFilterSelectOption = FilterBuilderAddFilterOption & {
    filterId: string;
};

type SavedFilterSelectGroup = FilterBuilderAddFilterGroup<SavedFilterSelectOption>;

type SourceDateFilters =
    | Pick<
          ApplicationFilters,
          "startDate" | "endDate" | "dateRangeLabel" | "dateReferenceOption"
      >
    | null;

type Props = {
    isVisible: boolean;
    store: BaseObjectStore<any, any>;
    objectFiltersStore: ApplicationFiltersInstanceStore;
    sourceStore: ApplicationFiltersInstanceStore | ApplicationFiltersStore;
    sourceDateStore: SourceDateFilters;
    onRemove: () => void;
    onApplied: () => void;
    syncSelectedFilterKeysFromStore: () => void;
};

function hasNonDefaultCallDuration(
    filters:
        | Pick<ApplicationFilters, "minCallDuration" | "maxCallDuration">
        | null
        | undefined,
) {
    return (filters?.minCallDuration ?? 0) > 0 || (filters?.maxCallDuration ?? 0) > 0;
}

function hasNonDefaultMessageCount(
    filters:
        | Pick<ApplicationFilters, "minMessageCount" | "maxMessageCount">
        | null
        | undefined,
) {
    return (
        (filters?.minMessageCount ?? 0) > 0 ||
        (filters?.maxMessageCount ?? 0) > 0
    );
}

const ObjectSavedFiltersSelector: React.FC<Props> = observer(
    ({
        isVisible,
        store,
        objectFiltersStore,
        sourceStore,
        sourceDateStore,
        onRemove,
        onApplied,
        syncSelectedFilterKeysFromStore,
    }) => {
        const applicationFiltersStore = useStore(ApplicationFiltersStore);
        const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
        const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
        const messageStore = useStore(MessageStore);
        const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<
            string | undefined
        >(undefined);
        const hasSourceDateStore = sourceDateStore !== null;

        const normalizeDate = useCallback((value: unknown) => {
            if (!value) return null;
            const parsedDate = moment(value as moment.MomentInput);
            if (!parsedDate.isValid()) return null;
            return parsedDate.valueOf();
        }, []);

        const doesDateRangeMatchSource = useCallback(
            (
                filters:
                    | Pick<
                          ApplicationFilters,
                          "startDate" | "endDate" | "dateRangeLabel"
                      >
                    | null
                    | undefined,
            ) => {
                if (!hasSourceDateStore || !filters) return false;

                const candidateStartDate = normalizeDate(filters.startDate);
                const sourceStartDate = normalizeDate(sourceDateStore.startDate);
                const candidateEndDate = normalizeDate(filters.endDate);
                const sourceEndDate = normalizeDate(sourceDateStore.endDate);
                const labelsMatch =
                    (filters.dateRangeLabel ?? "") ===
                    (sourceDateStore.dateRangeLabel ?? "");

                return (
                    candidateStartDate === sourceStartDate &&
                    candidateEndDate === sourceEndDate &&
                    labelsMatch
                );
            },
            [
                hasSourceDateStore,
                normalizeDate,
                sourceDateStore?.dateRangeLabel,
                sourceDateStore?.endDate,
                sourceDateStore?.startDate,
            ],
        );

        const normalizeSavedFilterValue = useCallback(
            (savedFilter: SavedFilter): ApplicationFilters => {
                const normalizedValue = _.cloneDeep(savedFilter.value);
                normalizedValue.extendedMetadataGrouped =
                    buildExtendedMetadataGroupedFromStrings(
                        normalizedValue.extendedMetadata,
                        normalizedValue.extendedMetadataGrouped,
                        applicationFilterItemsStore.extendedMetadataFields,
                    );
                return normalizedValue;
            },
            [applicationFilterItemsStore.extendedMetadataFields],
        );

        const syncObjectDateRangeToDashboard = useCallback(() => {
            if (!hasSourceDateStore) return;
            objectFiltersStore.setStartDate(sourceDateStore.startDate);
            objectFiltersStore.setEndDate(sourceDateStore.endDate);
            objectFiltersStore.setDateRangeLabel(
                sourceDateStore.dateRangeLabel ?? "",
            );
        }, [
            hasSourceDateStore,
            objectFiltersStore,
            sourceDateStore?.dateRangeLabel,
            sourceDateStore?.endDate,
            sourceDateStore?.startDate,
        ]);

        const allSavedFilters =
            applicationFiltersStore.getSavedFiltersByDomains(
                ApplicationFiltersStore.AllSavedFilterDomains,
            );

        const savedFilterConflictById = isVisible
            ? allSavedFilters.reduce<Record<string, boolean>>(
                  (nextConflictState, savedFilter) => {
                      const normalizedFilters =
                          normalizeSavedFilterValue(savedFilter);
                      const sanitizedFiltersResult =
                          applicationFilterItemsStore.stripInaccessibleFilters(
                              normalizedFilters,
                          );
                      const sanitizedFilters = sanitizedFiltersResult.filters;
                      const comparisonKeys = getObjectFilterKeysFromFilters(
                          sanitizedFilters,
                      );
                      nextConflictState[savedFilter.id] =
                          sourceStore.compareFiltersByKey(
                              sanitizedFilters,
                              comparisonKeys,
                              { agents: applicationFilterValuesStore.agents },
                          ).conflicts.length > 0;

                      return nextConflictState;
                  },
                  {},
              )
            : {};

        const savedFilterIdSet = new Set(
            allSavedFilters.map((filter) => filter.id),
        );
        const toSavedFilterOption = (
            savedFilter: SavedFilter,
        ): SavedFilterSelectOption => ({
            title: savedFilter.name,
            filterId: savedFilter.id,
            isDisabled: savedFilterConflictById[savedFilter.id] ?? false,
            disabledLabel:
                savedFilterConflictById[savedFilter.id]
                    ? "Conflicts with dashboard filters"
                    : undefined,
        });
        const savedFilterSelectGroups: SavedFilterSelectGroup[] = [
            {
                label: "My Filters",
                options: applicationFiltersStore.mySavedFilters
                    .filter((filter) => savedFilterIdSet.has(filter.id))
                    .map(toSavedFilterOption),
            },
            {
                label: "Shared Filters",
                options: applicationFiltersStore.sharedSavedFilters
                    .filter((filter) => savedFilterIdSet.has(filter.id))
                    .map(toSavedFilterOption),
            },
        ].filter((group) => group.options.length > 0);

        const applySavedFilterSelection = (filterId: string) => {
            const savedFilter = applicationFiltersStore.getSavedFilterById(filterId);
            if (!savedFilter) return;

            setSelectedSavedFilterId(filterId);

            const normalizedFilters = normalizeSavedFilterValue(savedFilter);
            const sanitizedFiltersResult =
                applicationFilterItemsStore.stripInaccessibleFilters(
                    normalizedFilters,
                );
            const sanitizedFilters = sanitizedFiltersResult.filters;
            const permissionsRemovedFilters =
                sanitizedFiltersResult.removedInaccessibleFilters;

            objectFiltersStore.applyExternalFilters(_.cloneDeep(sanitizedFilters));

            const hasSavedDateRange =
                !!sanitizedFilters.startDate && !!sanitizedFilters.endDate;
            const dateReferenceOption =
                sanitizedFilters.dateReferenceOption ??
                (
                    sanitizedFilters as ApplicationFilters & {
                        dateReference?: ApplicationFilters["dateReferenceOption"];
                    }
                ).dateReference;
            const sourceDateReferenceOption = sourceDateStore?.dateReferenceOption;

            store.objectFilterDateOverride =
                hasSavedDateRange &&
                (!hasSourceDateStore ||
                    !doesDateRangeMatchSource(sanitizedFilters));
            store.objectFilterDateTypeOverride =
                dateReferenceOption !== undefined &&
                (!hasSourceDateStore ||
                    dateReferenceOption !== sourceDateReferenceOption);
            store.objectFilterCallDurationOverride =
                hasNonDefaultCallDuration(sanitizedFilters);
            store.objectFilterMessageCountOverride =
                hasNonDefaultMessageCount(sanitizedFilters);

            if (!store.objectFilterDateOverride) {
                syncObjectDateRangeToDashboard();
            }

            if (
                !store.objectFilterDateTypeOverride &&
                sourceDateReferenceOption !== undefined
            ) {
                objectFiltersStore.setDateReferenceOption(
                    sourceDateReferenceOption,
                );
            }

            if (permissionsRemovedFilters) {
                messageStore.logWarning(
                    "Some filters were not applied from the Saved Filter due to permissions",
                );
            }

            setSelectedSavedFilterId(undefined);
            syncSelectedFilterKeysFromStore();
            onApplied();
        };

        if (!isVisible) return null;

        return (
            <Grid item xs key="saved-filter-section">
                <Grid
                    container
                    direction="column"
                    sx={{
                        width: "100%",
                        py: 0.5,
                        "& .object-filter-remove-slot": {
                            width: "24px",
                            marginLeft: 1,
                            flexShrink: 0,
                            opacity: 0,
                            visibility: "hidden",
                            transition: "opacity 120ms ease",
                        },
                        "&:hover .object-filter-remove-slot": {
                            opacity: 1,
                            visibility: "visible",
                        },
                    }}
                >
                    <Grid container alignItems="flex-start">
                        <Grid item xs sx={{ minWidth: 0, maxWidth: "100%" }}>
                            <Grid container direction="column" rowGap={1}>
                                <Grid item>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.gray[700],
                                            fontWeight: 500,
                                        }}
                                    >
                                        Saved Filter
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <FilterBuilderAddFilterSelect<SavedFilterSelectOption>
                                        id={`${store.id}-saved-object-filter`}
                                        options={savedFilterSelectGroups}
                                        placeholder={
                                            selectedSavedFilterId
                                                ? "Applying Saved Filter..."
                                                : "Select Saved Filter"
                                        }
                                        onChange={(option) => {
                                            if (!option) return;
                                            applySavedFilterSelection(
                                                option.filterId,
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Grid
                                        container
                                        alignItems="center"
                                        columnGap={0.75}
                                    >
                                        <Grid item>
                                            <InfoOutlined
                                                sx={{
                                                    fontSize: 18,
                                                    color: theme.palette.primary.main,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    whiteSpace: "normal",
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                This applies the saved filter once. It won't update if the saved filter is changed or deleted.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item className="object-filter-remove-slot">
                            <IconButton
                                id={`${store.id}-remove-saved-object-filter`}
                                size="small"
                                onClick={onRemove}
                                sx={{
                                    p: 0.125,
                                }}
                            >
                                <Cancel
                                    fontSize="small"
                                    htmlColor={theme.palette.gray[400]}
                                />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    },
);

export default ObjectSavedFiltersSelector;
